<template>
  <div class="ocr-config-container">
    <el-card>
      <div slot="header" class="clearfix">
        <span style="font-size: 18px; font-weight: bold;">{{ pageTitle }}</span>
      </div>
      
      <!-- 规则说明 -->
      <el-alert
        title="💡 免费额度说明"
        type="info"
        :closable="false"
        style="margin-bottom: 20px;">
        <div slot="default" style="line-height: 1.8;">
          <div style="margin-bottom: 8px;">
            <strong>• API类型说明：</strong>
            <span style="color: #409EFF; margin-left: 5px;">【智能轮询】</span> 推荐！自动选择免费额度未用完的API，单密钥可用1000次/月 | 
            <span style="color: #E6A23C; margin-left: 5px;">【高精版】</span> 200次/月 | 
            <span style="color: #67C23A; margin-left: 5px;">【手写体】</span> 200次/月 | 
            <span style="color: #409EFF; margin-left: 5px;">【通用文字】</span> 200次/月 | 
            <span style="color: #9C27B0; margin-left: 5px;">【多语言】</span> 200次/月 | 
            <span style="color: #F56C6C; margin-left: 5px;">【英语专项】</span> 200次/月
          </div>
          <div style="margin-bottom: 8px;">
            <strong>• 智能轮询优先级：</strong>
            <span style="color: #E6A23C;">全文识别高精版</span> → 
            <span style="color: #67C23A;">通用手写体识别</span> → 
            <span style="color: #409EFF;">通用文字识别</span> → 
            <span style="color: #9C27B0;">通用多语言识别</span> → 
            <span style="color: #F56C6C;">英语专项识别</span>
            （优先消耗高精版免费额度）
          </div>
          <div style="margin-bottom: 8px;">
            <strong>• 配置排序：</strong>
            点击 <el-tag size="mini" type="info"><i class="el-icon-top"></i></el-tag> <el-tag size="mini" type="info"><i class="el-icon-bottom"></i></el-tag> 按钮调整配置优先级，排在前面的配置优先使用
          </div>
          <div style="margin-bottom: 8px;">
            <strong>• 当前使用：</strong>
            配置名称前带 <el-tag type="warning" size="mini"><i class="el-icon-loading"></i> 当前使用</el-tag> 标签的即为您现在正在使用的OCR配置
          </div>
          <div>
            <strong>• 成本优化：</strong>
            配置3个密钥且全部设为“智能轮询”，可获得 <strong style="color: #E6A23C;">3000次/月</strong> 免费额度！每月1号自动重置计数器。
          </div>
        </div>
      </el-alert>
      
      <!-- 搜索和操作栏 -->
      <div class="toolbar">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索配置名称或备注"
          style="width: 300px; margin-right: 10px;"
          clearable
          @clear="getConfigList">
          <el-button slot="append" icon="el-icon-search" @click="getConfigList"></el-button>
        </el-input>
        
        <el-button type="primary" icon="el-icon-plus" @click="showAddDialog">新增配置</el-button>
        <el-button type="danger" icon="el-icon-delete" :disabled="selectedRows.length === 0" @click="batchDelete">批量删除</el-button>
      </div>
      
      <!-- 配置列表 -->
      <el-table
        :data="configList"
        v-loading="loading"
        stripe
        border
        row-key="id"
        @selection-change="handleSelectionChange"
        style="margin-top: 20px;">
        
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        
        <el-table-column label="排序" width="80" align="center">
          <template slot-scope="scope">
            <div style="display: flex; flex-direction: column; gap: 2px; align-items: center;">
              <el-button 
                type="text" 
                icon="el-icon-top" 
                size="mini"
                :disabled="scope.$index === 0"
                @click="moveUp(scope.$index)"
                style="padding: 0; margin: 0;">
              </el-button>
              <span style="font-size: 12px; color: #909399;">{{ scope.row.sortOrder || 0 }}</span>
              <el-button 
                type="text" 
                icon="el-icon-bottom" 
                size="mini"
                :disabled="scope.$index === configList.length - 1"
                @click="moveDown(scope.$index)"
                style="padding: 0; margin: 0;">
              </el-button>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="configName" label="配置名称" min-width="150">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.isDefault === 1" type="success" size="mini" style="margin-right: 8px;">默认</el-tag>
            <el-tag v-if="scope.row.id === currentConfigId" type="warning" size="mini" style="margin-right: 8px;">
              <i class="el-icon-loading" style="margin-right: 3px;"></i>当前使用
            </el-tag>
            {{ scope.row.configName }}
          </template>
        </el-table-column>
        
        <el-table-column label="本月使用" width="400" align="center">
          <template slot-scope="scope">
            <div>
              <!-- 总使用次数 -->
              <div style="margin-bottom: 5px; font-size: 13px; font-weight: bold; color: #303133;">
                总计：{{ scope.row.monthlyUsageCount || 0 }} / 1000 次
              </div>
              
              <!-- 分类型统计(5种类型,按优先级排序) -->
              <div style="display: flex; justify-content: space-around; font-size: 12px; color: #606266;">
                <div style="text-align: center;">
                  <div style="color: #E6A23C;">高精版</div>
                  <div>{{ scope.row.advancedMonthlyCount || 0 }}/200</div>
                </div>
                <div style="text-align: center;">
                  <div style="color: #67C23A;">手写体</div>
                  <div>{{ scope.row.handwritingMonthlyCount || 0 }}/200</div>
                </div>
                <div style="text-align: center;">
                  <div style="color: #409EFF;">通用</div>
                  <div>{{ scope.row.generalMonthlyCount || 0 }}/200</div>
                </div>
                <div style="text-align: center;">
                  <div style="color: #9C27B0;">多语言</div>
                  <div>{{ scope.row.multilanguageMonthlyCount || 0 }}/200</div>
                </div>
                <div style="text-align: center;">
                  <div style="color: #F56C6C;">英语</div>
                  <div>{{ scope.row.englishMonthlyCount || 0 }}/200</div>
                </div>
              </div>
              
              <!-- 总使用率进度条 -->
              <div style="margin-top: 8px;">
                <el-progress 
                  :percentage="getTotalUsagePercentage(scope.row)" 
                  :color="getTotalProgressColor(scope.row)"
                  :status="scope.row.monthlyUsageCount >= 1000 ? 'exception' : null"
                  :stroke-width="6">
                </el-progress>
              </div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="apiType" label="API类型" width="140" align="center">
          <template slot-scope="scope">
            <el-tag :type="getApiTypeTag(scope.row.apiType)" size="small">
              {{ getApiTypeName(scope.row.apiType) }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="isGlobalUse" label="全局使用" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isGlobalUse === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.isGlobalUse === 1 ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.status"
              :active-value="1"
              :inactive-value="0"
              active-color="#13ce66"
              inactive-color="#ff4949"
              @change="updateStatus(scope.row)">
            </el-switch>
          </template>
        </el-table-column>
        
        <el-table-column prop="accessKeyId" label="Access Key ID" min-width="200" show-overflow-tooltip></el-table-column>
        
        <el-table-column prop="accessKeySecret" label="Access Key Secret" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ maskSecret(scope.row.accessKeySecret) }}
          </template>
        </el-table-column>
        
        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip></el-table-column>
        
        <el-table-column prop="createTime" label="创建时间" width="180" align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createTime) }}
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="240" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click="showEditDialog(scope.row)" style="margin-right: 8px;">编辑</el-button><el-dropdown @command="handleCommand($event, scope.row)" trigger="click">
              <el-button size="mini">
                更多<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="'setDefault'" v-if="scope.row.isDefault !== 1">
                  <i class="el-icon-star-off"></i>
                  设为默认
                </el-dropdown-item>
                <el-dropdown-item :command="'toggleGlobal'">
                  <i :class="scope.row.isGlobalUse === 1 ? 'el-icon-close' : 'el-icon-check'"></i>
                  {{ scope.row.isGlobalUse === 1 ? '取消全局' : '设为全局' }}
                </el-dropdown-item>
                <el-dropdown-item :command="'delete'" divided>
                  <i class="el-icon-delete" style="color: #F56C6C;"></i>
                  <span style="color: #F56C6C;">删除</span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.pageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        style="margin-top: 20px; text-align: right;">
      </el-pagination>
    </el-card>
    
    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogMode === 'add' ? '新增OCR配置' : '编辑OCR配置'"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false">
      
      <el-form :model="currentConfig" :rules="rules" ref="configForm" label-width="150px">
        <el-form-item label="配置名称" prop="configName">
          <el-input v-model="currentConfig.configName" placeholder="请输入配置名称"></el-input>
        </el-form-item>
        
        <el-form-item label="Access Key ID" prop="accessKeyId">
          <el-input v-model="currentConfig.accessKeyId" placeholder="请输入Access Key ID"></el-input>
        </el-form-item>
        
        <el-form-item label="Access Key Secret" prop="accessKeySecret">
          <el-input 
            v-model="currentConfig.accessKeySecret" 
            type="password"
            placeholder="请输入Access Key Secret"
            show-password>
          </el-input>
        </el-form-item>
        
        <el-form-item label="区域端点" prop="regionId">
          <el-select v-model="currentConfig.regionId" placeholder="请选择区域端点" style="width: 100%;">
            <el-option label="华东1（杭州）" value="cn-hangzhou"></el-option>
            <el-option label="华北2（北京）" value="cn-beijing"></el-option>
            <el-option label="华东2（上海）" value="cn-shanghai"></el-option>
            <el-option label="华南1（深圳）" value="cn-shenzhen"></el-option>
          </el-select>
        </el-form-item>
        
       <el-form-item label="API类型" prop="apiType">
          <el-select v-model="currentConfig.apiType" placeholder="请选择API类型" style="width: 100%;">
            <el-option label="智能轮询（推荐）" value="AUTO">
              <span style="float: left">智能轮询</span>
              <span style="float: right; color: #409EFF; font-size: 12px;">★ 推荐</span>
            </el-option>
            <el-option label="全文识别高精版" value="ADVANCED">
              <span style="float: left">全文识别高精版</span>
              <span style="float: right; color: #E6A23C; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="通用手写体识别" value="HANDWRITING">
              <span style="float: left">通用手写体识别</span>
              <span style="float: right; color: #67C23A; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="通用文字识别" value="GENERAL">
              <span style="float: left">通用文字识别</span>
              <span style="float: right; color: #409EFF; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="通用多语言识别" value="MULTILANGUAGE">
              <span style="float: left">通用多语言识别</span>
              <span style="float: right; color: #9C27B0; font-size: 12px;">200次/月</span>
            </el-option>
            <el-option label="英语专项识别" value="ENGLISH">
              <span style="float: left">英语专项识别</span>
              <span style="float: right; color: #F56C6C; font-size: 12px;">200次/月</span>
            </el-option>
          </el-select>
          <div style="margin-top: 5px; color: #909399; font-size: 12px;">
            <div v-if="currentConfig.apiType === 'AUTO'">✨ 智能选择免费额度未用完的API，最大化利用免费额度（1000次/月）</div>
            <div v-else-if="currentConfig.apiType === 'ADVANCED'">📊 优先使用全文识别高精版，适合复杂文档，每月200次免费额度</div>
            <div v-else-if="currentConfig.apiType === 'HANDWRITING'">✍️ 优先使用通用手写体识别，适合手写文字，每月200次免费额度</div>
            <div v-else-if="currentConfig.apiType === 'GENERAL'">✅ 优先使用通用文字识别，适合一般文字，每月200次免费额度</div>
            <div v-else-if="currentConfig.apiType === 'MULTILANGUAGE'">🌍 优先使用通用多语言识别，支持100+语言，每月200次免费额度</div>
            <div v-else-if="currentConfig.apiType === 'ENGLISH'">🇬🇧 优先使用英语专项识别，适合英文文档，每月200次免费额度</div>
          </div>
        </el-form-item>
        
        <el-form-item label="状态">
          <el-radio-group v-model="currentConfig.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="设为默认配置">
          <el-switch
            v-model="currentConfig.isDefault"
            :active-value="1"
            :inactive-value="0">
          </el-switch>
        </el-form-item>
        
        <el-form-item label="备注">
          <el-input 
            v-model="currentConfig.remark" 
            type="textarea" 
            :rows="3"
            placeholder="请输入备注说明">
          </el-input>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveConfig" :loading="saving">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'OcrConfigManage',
  data() {
    return {
      pageTitle: 'OCR配置列表', // 默认标题
      searchKeyword: '',
      configList: [],
      currentConfigId: null, // 当前正在使用的配置ID
      selectedRows: [],
      loading: false,
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogMode: 'add', // add 或 edit
      saving: false,
      currentConfig: {
        id: null,
        configName: '',
        accessKeyId: '',
        accessKeySecret: '',
        regionId: 'cn-hangzhou',
        apiType: 'GENERAL', // 默认使用通用文字识别
        status: 1,
        isDefault: 0,
        remark: ''
      },
      rules: {
        configName: [
          { required: true, message: '请输入配置名称', trigger: 'blur' }
        ],
        accessKeyId: [
          { required: true, message: '请输入Access Key ID', trigger: 'blur' }
        ],
        accessKeySecret: [
          { required: true, message: '请输入Access Key Secret', trigger: 'blur' }
        ],
        regionId: [
          { required: true, message: '请选择区域端点', trigger: 'change' }
        ]
      }
    }
  },
  mounted() {
    this.loadPageTitle()
    this.getConfigList()
  },
  methods: {
    // 动态加载页面标题
    async loadPageTitle() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allMenus = menus.menus || []
          const currentMenu = allMenus.find(m => m.url === this.$route.path)
          if (currentMenu) {
            this.pageTitle = currentMenu.name
          }
        }
      } catch (error) {
        console.error('加载页面标题失败:', error)
      }
    },
    // 获取配置列表
    async getConfigList() {
      this.loading = true
      
      try {
        const token = localStorage.getItem('admin_token')
        const response = await axios.get('/admin/ocr/config/page', {
          params: {
            pageNum: this.pagination.pageNum,
            pageSize: this.pagination.pageSize,
            keyword: this.searchKeyword
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          // 新的响应结构包含page和currentConfigId
          const data = response.data.data
          
          // 兼容旧版本（直接返回page对象）和新版本（返回包含page和currentConfigId的对象）
          if (data.page) {
            // 新版本
            const page = data.page
            this.configList = page.records
            this.pagination.total = page.total
            this.currentConfigId = data.currentConfigId
          } else {
            // 旧版本兼容
            this.configList = data.records
            this.pagination.total = data.total
            this.currentConfigId = null
          }
        } else {
          this.$message.error(response.data.msg || '获取数据失败')
        }
      } catch (error) {
        console.error('获取配置列表失败', error)
        this.$message.error('获取数据失败：' + (error.response?.data?.msg || error.message))
      } finally {
        this.loading = false
      }
    },
    
    // 显示新增对话框
    showAddDialog() {
      this.dialogMode = 'add'
      this.currentConfig = {
        id: null,
        configName: '',
        accessKeyId: '',
        accessKeySecret: '',
        regionId: 'cn-hangzhou',
        apiType: 'GENERAL', // 默认使用通用文字识别
        status: 1,
        isDefault: 0,
        remark: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.configForm.clearValidate()
      })
    },
    
    // 显示编辑对话框
    showEditDialog(row) {
      this.dialogMode = 'edit'
      this.currentConfig = { ...row }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.configForm.clearValidate()
      })
    },
    
    // 保存配置
    saveConfig() {
      this.$refs.configForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        this.saving = true
        
        try {
          const token = localStorage.getItem('admin_token')
          const url = this.dialogMode === 'add' 
            ? '/admin/ocr/config/add' 
            : '/admin/ocr/config/update'
          
          const response = await axios({
            method: this.dialogMode === 'add' ? 'post' : 'put',
            url: url,
            data: this.currentConfig,
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          
          if (response.data.code === 200) {
            this.$message.success(this.dialogMode === 'add' ? '添加成功' : '更新成功')
            this.dialogVisible = false
            this.getConfigList()
          } else {
            this.$message.error(response.data.msg || '操作失败')
          }
        } catch (error) {
          console.error('保存配置失败', error)
          this.$message.error('操作失败：' + (error.response?.data?.msg || error.message))
        } finally {
          this.saving = false
        }
      })
    },
    
    // 删除配置
    deleteConfig(row) {
      this.$confirm('确定要删除该配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const token = localStorage.getItem('admin_token')
          const response = await axios.delete(`/admin/ocr/config/delete/${row.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          
          if (response.data.code === 200) {
            this.$message.success('删除成功')
            this.getConfigList()
          } else {
            this.$message.error(response.data.msg || '删除失败')
          }
        } catch (error) {
          console.error('删除配置失败', error)
          this.$message.error('删除失败：' + (error.response?.data?.msg || error.message))
        }
      }).catch(() => {})
    },
    
    // 批量删除
    batchDelete() {
      this.$confirm(`确定要删除选中的 ${this.selectedRows.length} 条配置吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const token = localStorage.getItem('admin_token')
          const ids = this.selectedRows.map(row => row.id)
          
          const response = await axios.delete('/admin/ocr/config/deleteBatch', {
            data: ids,
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          
          if (response.data.code === 200) {
            this.$message.success('批量删除成功')
            this.getConfigList()
          } else {
            this.$message.error(response.data.msg || '删除失败')
          }
        } catch (error) {
          console.error('批量删除失败', error)
          this.$message.error('删除失败：' + (error.response?.data?.msg || error.message))
        }
      }).catch(() => {})
    },
    
    // 更新状态
    async updateStatus(row) {
      try {
        const token = localStorage.getItem('admin_token')
        const response = await axios.put('/admin/ocr/config/updateStatus', null, {
          params: {
            id: row.id,
            status: row.status
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          this.$message.success('状态更新成功')
        } else {
          this.$message.error(response.data.msg || '更新失败')
          // 恢复原状态
          row.status = row.status === 1 ? 0 : 1
        }
      } catch (error) {
        console.error('更新状态失败', error)
        this.$message.error('更新失败：' + (error.response?.data?.msg || error.message))
        // 恢复原状态
        row.status = row.status === 1 ? 0 : 1
      }
    },
    
    // 设置为默认配置
    setDefault(row) {
      this.$confirm('确定要将此配置设为默认配置吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          const token = localStorage.getItem('admin_token')
          const response = await axios.put(`/admin/ocr/config/setDefault/${row.id}`, null, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          
          if (response.data.code === 200) {
            this.$message.success('设置成功')
            this.getConfigList()
          } else {
            this.$message.error(response.data.msg || '设置失败')
          }
        } catch (error) {
          console.error('设置默认配置失败', error)
          this.$message.error('设置失败：' + (error.response?.data?.msg || error.message))
        }
      }).catch(() => {})
    },
    
    // 切换全局使用状态
    toggleGlobalUse(row) {
      const newValue = row.isGlobalUse === 1 ? 0 : 1
      const action = newValue === 1 ? '设置为全局使用' : '取消全局使用'
      
      this.$confirm(`确定要${action}吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const token = localStorage.getItem('admin_token')
          const response = await axios.put('/admin/ocr/config/updateGlobalUse', null, {
            params: {
              id: row.id,
              isGlobalUse: newValue
            },
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          
          if (response.data.code === 200) {
            this.$message.success('更新成功')
            this.getConfigList()
          } else {
            this.$message.error(response.data.msg || '更新失败')
          }
        } catch (error) {
          console.error('更新全局使用状态失败', error)
          this.$message.error('更新失败：' + (error.response?.data?.msg || error.message))
        }
      }).catch(() => {})
    },
    
    // 处理下拉菜单命令
    handleCommand(command, row) {
      switch(command) {
        case 'setDefault':
          this.setDefault(row)
          break
        case 'toggleGlobal':
          this.toggleGlobalUse(row)
          break
        case 'delete':
          this.deleteConfig(row)
          break
      }
    },
    
    // 选择改变
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    
    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.getConfigList()
    },
    
    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.pageNum = page
      this.getConfigList()
    },
    
    // 格式化日期时间
    formatDateTime(datetime) {
      if (!datetime) return ''
      const date = new Date(datetime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    // 脱敏显示密钥
    maskSecret(secret) {
      if (!secret) return ''
      if (secret.length <= 8) {
        return '***'
      }
      return secret.substring(0, 4) + '***' + secret.substring(secret.length - 4)
    },
    
    // 计算使用率百分比（总额度1000次）
    getUsagePercentage(row) {
      const count = row.monthlyUsageCount || 0
      return Math.min((count / 1000) * 100, 100)
    },
    
    // 计算总使用率百分比
    getTotalUsagePercentage(row) {
      const count = row.monthlyUsageCount || 0
      return Math.min((count / 1000) * 100, 100)
    },
    
    // 获取进度条颜色
    getProgressColor(row) {
      const count = row.monthlyUsageCount || 0
      if (count >= 1000) return '#F56C6C'  // 红色 - 已用完
      if (count >= 900) return '#E6A23C'  // 橙色 - 90%以上
      return '#67C23A'  // 绿色 - 90%以下
    },
    
    // 获取总进度条颜色
    getTotalProgressColor(row) {
      const count = row.monthlyUsageCount || 0
      if (count >= 1000) return '#F56C6C'  // 红色 - 已用完
      if (count >= 900) return '#E6A23C'  // 橙色 - 90%以上
      return '#67C23A'  // 绿色 - 90%以下
    },
    
    // 获取API类型名称
    getApiTypeName(apiType) {
      const typeMap = {
        'AUTO': '智能轮询',
        'ADVANCED': '高精版',
        'HANDWRITING': '手写体',
        'GENERAL': '通用文字',
        'MULTILANGUAGE': '多语言',
        'ENGLISH': '英语专项'
      }
      return typeMap[apiType] || '智能轮询'
    },
    
    // 获取API类型标签颜色
    getApiTypeTag(apiType) {
      const tagMap = {
        'AUTO': '',           // 蓝色 - 智能模式
        'ADVANCED': 'warning',  // 橙色 - 高精版
        'HANDWRITING': 'success', // 绿色 - 手写体
        'GENERAL': '',          // 蓝色 - 通用
        'MULTILANGUAGE': '',    // 蓝色 - 多语言
        'ENGLISH': 'danger'     // 红色 - 英语
      }
      return tagMap[apiType] || ''
    },
    
    // 上移配置
    async moveUp(index) {
      if (index === 0) return
      
      const current = this.configList[index]
      const prev = this.configList[index - 1]
      
      // 交换sortOrder
      const tempSort = current.sortOrder || 0
      current.sortOrder = prev.sortOrder || 0
      prev.sortOrder = tempSort
      
      // 交换数组位置(本地更新UI)
      this.configList.splice(index - 1, 2, current, prev)
      
      // 批量更新排序到后端
      await this.saveSortOrder()
    },
    
    // 下移配置
    async moveDown(index) {
      if (index === this.configList.length - 1) return
      
      const current = this.configList[index]
      const next = this.configList[index + 1]
      
      // 交换sortOrder
      const tempSort = current.sortOrder || 0
      current.sortOrder = next.sortOrder || 0
      next.sortOrder = tempSort
      
      // 交换数组位置(本地更新UI)
      this.configList.splice(index, 2, next, current)
      
      // 批量更新排序到后端
      await this.saveSortOrder()
    },
    
    // 保存排序到后端
    async saveSortOrder() {
      try {
        const token = localStorage.getItem('admin_token')
        const sortList = this.configList.map(config => ({
          id: config.id,
          sortOrder: config.sortOrder || 0
        }))
        
        const response = await axios.put('/admin/ocr/config/updateSortOrder', sortList, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.data.code === 200) {
          this.$message.success('排序更新成功')
          // 刷新列表以同步"当前使用"标识
          this.getConfigList()
        } else {
          this.$message.error(response.data.msg || '排序更新失败')
        }
      } catch (error) {
        console.error('保存排序失败', error)
        this.$message.error('排序更新失败：' + (error.response?.data?.msg || error.message))
      }
    }
  }
}
</script>

<style scoped>
.ocr-config-container {
  padding: 20px;
}

.toolbar {
  display: flex;
  align-items: center;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}
</style>
